import {Player, PlayerForm, PlayerPatchData} from '../types/Player';
import baseGoApi from "@/api/baseGoApi.ts";
import { searchPlayerByEmailOrCreate } from "@/api/chatApi";

// Define the response type for the collection of players
export interface PlayerCollectionResponse {
    '@context': string;
    '@id': string;
    '@type': string;
    'member': Player[];
    'totalItems': number;
}

// Fetch all players. The backend caps each response at playerListMaxLimit
// (500), so we page through until X-Total-Count is satisfied. This keeps the
// Redux player slice — used by the booking form, group training form, course
// manager, etc. — complete regardless of org size.
export const fetchPlayers = async (): Promise<Player[]> => {
    const pageSize = 500;
    const all: Player[] = [];
    let offset = 0;
    // Cap the loop defensively in case the backend ever stops returning
    // X-Total-Count; pageSize * maxIterations gives a hard ceiling.
    const maxIterations = 50;

    for (let i = 0; i < maxIterations; i++) {
        const response = await baseGoApi.get<Player[]>('/player/', {
            params: { limit: pageSize, offset, orderBy: 'name:asc' },
        });
        all.push(...response.data);

        const headerTotal =
            response.headers?.['x-total-count'] ?? response.headers?.['X-Total-Count'];
        const total = headerTotal !== undefined ? parseInt(String(headerTotal), 10) : NaN;

        if (response.data.length < pageSize) break;
        if (Number.isFinite(total) && all.length >= total) break;

        offset += pageSize;
    }

    return all;
};

// Server-paginated player listing for /players. Mirrors the Go endpoint:
//   GET /player/?limit=&offset=&search=&pinnedOnly=&orderBy=
// Total count is exposed via the X-Total-Count response header.
export interface PaginatedPlayersParams {
    limit: number;
    offset: number;
    search?: string;
    pinnedOnly?: boolean;
    orderBy?: string;
}

export interface PaginatedPlayersResponse {
    players: Player[];
    total: number;
}

export const fetchPlayersPaginated = async (
    params: PaginatedPlayersParams,
): Promise<PaginatedPlayersResponse> => {
    const query: Record<string, string | number | boolean> = {
        limit: params.limit,
        offset: params.offset,
        orderBy: params.orderBy ?? "name:asc",
    };
    if (params.search && params.search.trim()) {
        query.search = params.search.trim();
    }
    if (params.pinnedOnly) {
        query.pinnedOnly = true;
    }

    const response = await baseGoApi.get<Player[]>('/player/', { params: query });
    const headerTotal =
        response.headers?.['x-total-count'] ?? response.headers?.['X-Total-Count'];
    const parsed = headerTotal !== undefined ? parseInt(String(headerTotal), 10) : NaN;
    const total = Number.isFinite(parsed)
        ? parsed
        : params.offset + response.data.length;

    return { players: response.data, total };
};

// Fetch a single player by ID
export const fetchPlayerById = async (id: number): Promise<Player> => {
    const response = await baseGoApi.get<Player>(`/player/${id}`);
    return response.data;
};

// Create a new player
export const createPlayer = async (playerData: Partial<PlayerForm>): Promise<Player> => {
    const response = await baseGoApi.post<Player>('/player/', playerData);
    return response.data;
};

// Update an existing player
export const updatePlayer = async (id: number, playerData: Partial<PlayerForm>): Promise<Player> => {
    const response = await baseGoApi.put<Player>(`/player/${id}`, playerData);
    return response.data;
};

// Patch a player (partial update)
export const patchPlayer = async (id: number, playerData: PlayerPatchData): Promise<Player> => {
    const response = await baseGoApi.patch<Player>(`/player/${id}`, playerData,{
        headers: {
            "Content-Type": "application/merge-patch+json",
        },
    });
    return response.data;
};

// Delete a player
export const deletePlayer = async (id: number): Promise<void> => {
    await baseGoApi.delete(`/player/${id}`);
};

export interface MergePlayersResult {
    success: boolean;
    message: string;
    /** False when the player's Player Hub data (app profile, training) could not be moved. */
    playerHubSynced: boolean;
    /** Set when playerHubSynced is false — show it to the trainer. */
    warning?: string;
}

// Merge two players: all data from removedPlayerId is moved to masterPlayerId, then removed player is hard deleted
export const mergePlayers = async (masterPlayerId: number, removedPlayerId: number): Promise<MergePlayersResult> => {
    const response = await baseGoApi.post('/player/merge', { masterPlayerId, removedPlayerId });
    return response.data;
};

/**
 * Ensure the player has a playerHubId: search in Player Hub by email, create if missing, then PATCH go-api.
 * Returns the playerHubId to use for Player Hub APIs (e.g. exercises).
 */
export const ensurePlayerHubId = async (player: Player): Promise<string> => {
    if (player.playerHubId) return player.playerHubId;
    const result = await searchPlayerByEmailOrCreate(player.email, player.name);
    const playerHubId = result.user_id;
    if (!playerHubId) throw new Error("Failed to resolve or create player hub profile");
    await patchPlayer(player.id, { playerHubId });
    return playerHubId;
};

// Fetch latest screening for a player
export const fetchLatestScreening = async (playerId: number): Promise<any> => {
    const response = await baseGoApi.get(`/classic-screening/player/${playerId}/latest`);
    return response.data;
};

// Fetch player goals
export const fetchPlayerGoals = async (playerId: number): Promise<any[]> => {
    const response = await baseGoApi.get(`/player-objectives/player/${playerId}`);
    return response.data;
};

// Fetch player trainings
export const fetchPlayerTrainings = async (playerId: number): Promise<any[]> => {
    const response = await baseGoApi.get(`/player/${playerId}/playertraining`);
    return response.data;
};

// Fetch player appointments
export const fetchPlayerAppointments = async (playerId: number): Promise<any[]> => {
    const response = await baseGoApi.get(`/player/${playerId}/bookingplayerappointments`);
    return response.data;
};

// Fetch player packages
export const fetchPlayerPackages = async (playerId: number): Promise<any[]> => {
    const response = await baseGoApi.get(`/player/${playerId}/playerpackages`);
    return response.data;
};

// Fetch player screenings
export const fetchPlayerScreenings = async (playerId: number): Promise<any[]> => {
    const response = await baseGoApi.get(`/player/${playerId}/screening`);
    return response.data;
};

// Batch import players
export const batchImportPlayers = async (players: any[]): Promise<any> => {
    const response = await baseGoApi.post('/player/batch-import', players);
    return response.data;
};

// Fetch player notes
export const fetchPlayerNotes = async (playerId: number): Promise<any[]> => {
    const response = await baseGoApi.get(`/player/${playerId}/note`);
    return response.data;
};

// Get a single player note
export const fetchPlayerNoteById = async (playerId: number, noteId: number): Promise<any> => {
    const response = await baseGoApi.get(`/player/${playerId}/note/${noteId}`);
    return response.data;
};

// Create a player note
export const createPlayerNote = async (playerId: number, noteData: any): Promise<any> => {
    const response = await baseGoApi.post(`/player/${playerId}/note`, noteData);
    return response.data;
};

// Update a player note
export const updatePlayerNote = async (playerId: number, noteId: number, noteData: any): Promise<any> => {
    const response = await baseGoApi.put(`/player/${playerId}/note/${noteId}`, noteData);
    return response.data;
};

// Delete a player note
export const deletePlayerNote = async (playerId: number, noteId: number): Promise<void> => {
    await baseGoApi.delete(`/player/${playerId}/note/${noteId}`);
};

// Check if a player exists by email
export interface PlayerCheckResult {
    id: number;
    email: string;
    name: string;
    mobile?: string;
    lang?: string;
    hcp?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface CheckPlayerByEmailResponse {
    exists: boolean;
    player?: PlayerCheckResult;
}

export const checkPlayerByEmail = async (email: string): Promise<CheckPlayerByEmailResponse> => {
    const response = await baseGoApi.post<CheckPlayerByEmailResponse>('/player/check-by-email', { email });
    return response.data;
};

// Create player affiliation (add existing player to your register)
export const createPlayerAffiliation = async (playerID: number): Promise<unknown> => {
    const response = await baseGoApi.post('/player/affiliations', { playerID });
    return response.data;
};

// Pin a player for the authenticated coach
export const pinPlayer = async (playerId: number): Promise<{ isPinned: boolean }> => {
    const response = await baseGoApi.post<{ isPinned: boolean }>(`/player/${playerId}/pin`);
    return response.data;
};

// Unpin a player for the authenticated coach
export const unpinPlayer = async (playerId: number): Promise<{ isPinned: boolean }> => {
    const response = await baseGoApi.delete<{ isPinned: boolean }>(`/player/${playerId}/pin`);
    return response.data;
};
